import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  PlusIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  BellAlertIcon,
  CalendarDaysIcon,
  FunnelIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { scheduleService, ScheduledEvent, CreateEventData } from '@/services/scheduleService';
import { userService } from '@/services/userService';
import { projectService } from '@/services/projectService';
import { useNotificationStore } from '@/store/notificationStore';
import { Button } from '@/components/common/Button';
import { Modal } from '@/components/common/Modal';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';

const STATUS_STYLES: Record<string, { bg: string; text: string; icon: any }> = {
  pending: { bg: 'bg-yellow-50 border-yellow-200', text: 'text-yellow-700', icon: ClockIcon },
  in_progress: { bg: 'bg-blue-50 border-blue-200', text: 'text-blue-700', icon: ArrowPathIcon },
  completed: { bg: 'bg-green-50 border-green-200', text: 'text-green-700', icon: CheckCircleIcon },
  overdue: { bg: 'bg-red-50 border-red-200', text: 'text-red-700', icon: ExclamationTriangleIcon },
  skipped: { bg: 'bg-gray-50 border-gray-200', text: 'text-gray-500', icon: ClockIcon },
};

const CATEGORY_EMOJI: Record<string, string> = {
  sprint: '🏃',
  content: '🎬',
  compliance: '⚖️',
  personal: '💪',
  meeting: '📞',
  deadline: '⏰',
};

const FREQUENCY_LABELS: Record<string, string> = {
  once: 'One-time',
  daily: 'Daily',
  weekly: 'Weekly',
  biweekly: 'Every 2 weeks',
  monthly: 'Monthly',
  quarterly: 'Quarterly',
};

const NAG_LEVEL_STYLES: Record<string, string> = {
  gentle: 'bg-blue-100 text-blue-700',
  firm: 'bg-yellow-100 text-yellow-700',
  urgent: 'bg-orange-100 text-orange-700',
  escalated: 'bg-red-100 text-red-700',
};

type ViewTab = 'upcoming' | 'all' | 'overdue';

export function SchedulePage() {
  const queryClient = useQueryClient();
  const addNotification = useNotificationStore((s) => s.addNotification);

  const [tab, setTab] = useState<ViewTab>('upcoming');
  const [showCreate, setShowCreate] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('');

  // Form state
  const [formTitle, setFormTitle] = useState('');
  const [formDesc, setFormDesc] = useState('');
  const [formDue, setFormDue] = useState('');
  const [formFreq, setFormFreq] = useState('once');
  const [formCategory, setFormCategory] = useState('');
  const [formProjectId, setFormProjectId] = useState('');
  const [formAssigneeId, setFormAssigneeId] = useState('');
  const [formNagEnabled, setFormNagEnabled] = useState(true);
  const [formNagHours, setFormNagHours] = useState(24);
  const [formNagChannels, setFormNagChannels] = useState<string[]>(['telegram']);

  // Queries
  const { data: upcoming = [], isLoading: upcomingLoading } = useQuery({
    queryKey: ['schedule', 'upcoming'],
    queryFn: () => scheduleService.getUpcoming(14),
    enabled: tab === 'upcoming',
  });

  const { data: allEvents, isLoading: allLoading } = useQuery({
    queryKey: ['schedule', 'all', categoryFilter],
    queryFn: () => scheduleService.getEvents(categoryFilter ? { category: categoryFilter } : undefined),
    enabled: tab === 'all',
  });

  const { data: overdue = [], isLoading: overdueLoading } = useQuery({
    queryKey: ['schedule', 'overdue'],
    queryFn: () => scheduleService.getOverdue(),
    enabled: tab === 'overdue',
  });

  const { data: projects = [] } = useQuery({
    queryKey: ['projects'],
    queryFn: () => projectService.getProjects(),
  });

  const { data: users = [] } = useQuery({
    queryKey: ['users'],
    queryFn: () => userService.getUsers(),
  });

  const createMutation = useMutation({
    mutationFn: (data: CreateEventData) => scheduleService.createEvent(data),
    onSuccess: () => {
      addNotification({ type: 'success', title: 'Event Created', message: 'Scheduled event created.' });
      queryClient.invalidateQueries({ queryKey: ['schedule'] });
      setShowCreate(false);
      resetForm();
    },
    onError: (e: any) => {
      addNotification({ type: 'error', title: 'Error', message: e?.response?.data?.message || 'Failed to create event' });
    },
  });

  const completeMutation = useMutation({
    mutationFn: (id: string) => scheduleService.completeEvent(id),
    onSuccess: (event) => {
      addNotification({ type: 'success', title: 'Completed!', message: `"${event.title}" marked as complete.` });
      queryClient.invalidateQueries({ queryKey: ['schedule'] });
    },
  });

  const nagMutation = useMutation({
    mutationFn: () => scheduleService.runNagCheck(),
    onSuccess: (result) => {
      addNotification({
        type: 'success',
        title: 'Nag Run Complete',
        message: `${result.nags_sent} reminder(s) sent.`,
      });
    },
  });

  const resetForm = () => {
    setFormTitle('');
    setFormDesc('');
    setFormDue('');
    setFormFreq('once');
    setFormCategory('');
    setFormProjectId('');
    setFormAssigneeId('');
    setFormNagEnabled(true);
    setFormNagHours(24);
    setFormNagChannels(['telegram']);
  };

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    createMutation.mutate({
      title: formTitle,
      description: formDesc || undefined,
      due_at: new Date(formDue).toISOString(),
      frequency: formFreq,
      category: formCategory || undefined,
      project_id: formProjectId || undefined,
      assignee_id: formAssigneeId || undefined,
      nag_enabled: formNagEnabled,
      nag_channels: formNagChannels,
      nag_interval_hours: formNagHours,
    });
  };

  const formatTime = (isoStr: string) => {
    const d = new Date(isoStr);
    return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr + 'T00:00:00');
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (dateStr === today.toISOString().split('T')[0]) return 'Today';
    if (dateStr === tomorrow.toISOString().split('T')[0]) return 'Tomorrow';
    return d.toLocaleDateString('en-GB', { weekday: 'long', month: 'short', day: 'numeric' });
  };

  const getProjectName = (projectId: string | null) => {
    if (!projectId) return null;
    const p = projects.find((pr) => pr.id === projectId);
    return p?.name || p?.key || null;
  };

  const renderEvent = (event: ScheduledEvent) => {
    const style = STATUS_STYLES[event.status] || STATUS_STYLES.pending;
    const StatusIcon = style.icon;
    const catEmoji = CATEGORY_EMOJI[event.category || ''] || '📋';

    return (
      <div key={event.id} className={`border rounded-lg p-4 ${style.bg} transition-all hover:shadow-md`}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-lg">{catEmoji}</span>
              <h3 className="font-semibold text-gray-900">{event.title}</h3>
            </div>
            {event.description && (
              <p className="text-sm text-gray-600 mt-1 line-clamp-2">{event.description}</p>
            )}
            <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-gray-500">
              <span className="flex items-center gap-1">
                <ClockIcon className="h-3.5 w-3.5" />
                {formatTime(event.due_at)}
              </span>
              <span className="flex items-center gap-1">
                <ArrowPathIcon className="h-3.5 w-3.5" />
                {FREQUENCY_LABELS[event.frequency] || event.frequency}
              </span>
              {getProjectName(event.project_id) && (
                <span className="bg-white/50 px-2 py-0.5 rounded">
                  📁 {getProjectName(event.project_id)}
                </span>
              )}
              {event.nag_enabled && event.nag_count > 0 && (
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${NAG_LEVEL_STYLES[event.nag_level] || ''}`}>
                  <BellAlertIcon className="h-3 w-3" />
                  {event.nag_level} (×{event.nag_count})
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {event.status !== 'completed' && event.status !== 'skipped' && (
              <button
                onClick={() => completeMutation.mutate(event.id)}
                className="p-2 text-green-600 hover:bg-green-100 rounded-lg transition-colors"
                title="Mark as complete"
              >
                <CheckCircleIcon className="h-6 w-6" />
              </button>
            )}
            <StatusIcon className={`h-5 w-5 ${style.text}`} />
          </div>
        </div>
      </div>
    );
  };

  const isLoading = (tab === 'upcoming' && upcomingLoading) || (tab === 'all' && allLoading) || (tab === 'overdue' && overdueLoading);

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Schedule & Calendar</h1>
          <p className="text-gray-600">Events, deadlines, and the nag engine</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            leftIcon={<BellAlertIcon className="h-5 w-5" />}
            onClick={() => nagMutation.mutate()}
            isLoading={nagMutation.isPending}
          >
            Run Nags Now
          </Button>
          <Button
            variant="primary"
            leftIcon={<PlusIcon className="h-5 w-5" />}
            onClick={() => { resetForm(); setShowCreate(true); }}
          >
            New Event
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-6 border-b">
        <nav className="flex gap-4">
          {[
            { id: 'upcoming', label: 'Upcoming', icon: CalendarDaysIcon },
            { id: 'overdue', label: 'Overdue', icon: ExclamationTriangleIcon, count: overdue.length },
            { id: 'all', label: 'All Events', icon: FunnelIcon },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id as ViewTab)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                tab === t.id
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <t.icon className="h-4 w-4" />
              {t.label}
              {t.count !== undefined && t.count > 0 && (
                <span className="bg-red-100 text-red-700 text-xs px-2 py-0.5 rounded-full">{t.count}</span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      ) : tab === 'upcoming' ? (
        <div className="space-y-6">
          {upcoming.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <CalendarDaysIcon className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p>Nothing scheduled for the next 14 days.</p>
            </div>
          ) : (
            upcoming.map((day) => (
              <div key={day.date}>
                <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <CalendarDaysIcon className="h-5 w-5 text-gray-400" />
                  {formatDate(day.date)}
                  <span className="text-sm font-normal text-gray-400">({day.events.length})</span>
                </h2>
                <div className="space-y-2 ml-7">
                  {day.events.map(renderEvent)}
                </div>
              </div>
            ))
          )}
        </div>
      ) : tab === 'overdue' ? (
        <div className="space-y-2">
          {overdue.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <CheckCircleIcon className="h-12 w-12 mx-auto mb-3 text-green-300" />
              <p>No overdue events! 🎉</p>
            </div>
          ) : (
            overdue.map(renderEvent)
          )}
        </div>
      ) : (
        <div>
          {/* Category filter for All tab */}
          <div className="mb-4">
            <select
              className="border rounded-lg px-3 py-2 text-sm"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <option value="">All categories</option>
              <option value="sprint">🏃 Sprint</option>
              <option value="content">🎬 Content</option>
              <option value="compliance">⚖️ Compliance</option>
              <option value="personal">💪 Personal</option>
              <option value="meeting">📞 Meeting</option>
              <option value="deadline">⏰ Deadline</option>
            </select>
          </div>
          <div className="space-y-2">
            {(allEvents?.data || []).map(renderEvent)}
            {(allEvents?.data || []).length === 0 && (
              <div className="text-center py-12 text-gray-500">No events found.</div>
            )}
          </div>
        </div>
      )}

      {/* Create Event Modal */}
      <Modal isOpen={showCreate} onClose={() => setShowCreate(false)} title="Create Scheduled Event" size="lg">
        <form onSubmit={handleCreate} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
            <input className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500" value={formTitle} onChange={(e) => setFormTitle(e.target.value)} required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500" rows={3} value={formDesc} onChange={(e) => setFormDesc(e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Due Date & Time *</label>
              <input type="datetime-local" className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500" value={formDue} onChange={(e) => setFormDue(e.target.value)} required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Frequency</label>
              <select className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500" value={formFreq} onChange={(e) => setFormFreq(e.target.value)}>
                {Object.entries(FREQUENCY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <select className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500" value={formCategory} onChange={(e) => setFormCategory(e.target.value)}>
                <option value="">None</option>
                <option value="sprint">🏃 Sprint</option>
                <option value="content">🎬 Content</option>
                <option value="compliance">⚖️ Compliance</option>
                <option value="personal">💪 Personal</option>
                <option value="meeting">📞 Meeting</option>
                <option value="deadline">⏰ Deadline</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Project</label>
              <select className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500" value={formProjectId} onChange={(e) => setFormProjectId(e.target.value)}>
                <option value="">None</option>
                {projects.map((p) => <option key={p.id} value={p.id}>{p.key} — {p.name}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Assignee</label>
            <select className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500" value={formAssigneeId} onChange={(e) => setFormAssigneeId(e.target.value)}>
              <option value="">Me (default)</option>
              {users.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.email})</option>)}
            </select>
          </div>

          {/* Nag Settings */}
          <div className="border rounded-lg p-4 bg-gray-50">
            <div className="flex items-center gap-2 mb-3">
              <BellAlertIcon className="h-5 w-5 text-gray-500" />
              <h3 className="font-medium text-gray-900">Nag Settings</h3>
            </div>
            <div className="space-y-3">
              <label className="flex items-center gap-2">
                <input type="checkbox" className="rounded" checked={formNagEnabled} onChange={(e) => setFormNagEnabled(e.target.checked)} />
                <span className="text-sm text-gray-700">Enable nagging (chase until done)</span>
              </label>
              {formNagEnabled && (
                <>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Nag every (hours)</label>
                    <input type="number" min={1} max={168} className="w-24 border rounded px-2 py-1 text-sm" value={formNagHours} onChange={(e) => setFormNagHours(Number(e.target.value))} />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Channels</label>
                    <div className="flex gap-3">
                      {['telegram', 'whatsapp', 'email'].map((ch) => (
                        <label key={ch} className="flex items-center gap-1.5">
                          <input
                            type="checkbox"
                            className="rounded"
                            checked={formNagChannels.includes(ch)}
                            onChange={(e) => {
                              if (e.target.checked) setFormNagChannels([...formNagChannels, ch]);
                              else setFormNagChannels(formNagChannels.filter((c) => c !== ch));
                            }}
                          />
                          <span className="text-sm capitalize">{ch}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="secondary" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button variant="primary" type="submit" isLoading={createMutation.isPending}>Create Event</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

export default SchedulePage;
